import { useState } from 'react';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { toast } from 'sonner';

import { useAdminPatchLoanApplicationMutation } from '@/apis/extended/loanApplicationApi';
import type { LoanApplication } from '@/types/LoanApplicationType';
import { LOAN_STATUSES } from './LoanFileAdminControls';

const labelOf = (status: string) =>
  LOAN_STATUSES.find(([v]) => v === status)?.[1] ?? status;

/**
 * Status right in the pipeline row, so changing it doesn't mean opening the
 * file. A change is confirmed first; emailing the borrower is opt-in.
 */
export function LoanStatusQuickSelect({ app }: { app: LoanApplication }) {
  const [patch, { isLoading }] = useAdminPatchLoanApplicationMutation();
  const [next, setNext] = useState<LoanApplication['status'] | null>(null);
  const [notify, setNotify] = useState(false);

  const close = () => {
    setNext(null);
    setNotify(false);
  };

  const save = async () => {
    if (!next) return;
    try {
      await patch({
        id: app._id,
        status: next,
        notifyBorrower: notify,
      }).unwrap();
      toast.success(
        notify ? 'Status updated · borrower emailed' : 'Status updated'
      );
      close();
    } catch {
      toast.error('Update failed');
    }
  };

  return (
    <>
      <TextField
        select
        size="small"
        variant="standard"
        value={app.status}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => {
          const v = e.target.value as LoanApplication['status'];
          if (v !== app.status) setNext(v);
        }}
        inputProps={{ 'aria-label': 'Loan status' }}
        sx={{ minWidth: 110 }}
      >
        {LOAN_STATUSES.map(([v, l]) => (
          <MenuItem key={v} value={v}>
            {l}
          </MenuItem>
        ))}
      </TextField>
      <Dialog
        open={next !== null}
        onClose={close}
        onClick={(e) => e.stopPropagation()}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Change status to {next ? labelOf(next) : ''}?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {app.property?.subjectPropertyAddress || 'This loan file'} is{' '}
            {labelOf(app.status).toLowerCase()} now.
            {next === 'approved'
              ? ' Open the file first if you need to set the note rate and terms.'
              : ''}
          </Typography>
          <FormControlLabel
            sx={{ mt: 1 }}
            control={
              <Checkbox
                size="small"
                checked={notify}
                onChange={(e) => setNotify(e.target.checked)}
              />
            }
            label="Email the borrower"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={close} color="inherit">
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => void save()}
            disabled={isLoading}
          >
            {isLoading ? 'Updating…' : 'Update status'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
